import React, {useEffect, useMemo, useRef, useState} from 'react';
import type {WdiModalAppProps} from '@/components/WdiControls';
import {FieldProps} from '@/components/WdiControls';
import type {ProFormInstance} from '@ant-design/pro-form';
import {EC} from '@/services/mdm/ec';
import {WdiSelect, WdiSelectFormat, WdiTreeSelect} from '@/components/WdiControls/WdiSelect';
import {Form, Input, message, Tooltip} from 'antd';
import {HorzLayoutOpts, WdiModalForm} from '@/components/WdiControls/WdiModalForm';
import {ResponseStatus} from '@/utils/request';
import {WdiInputNumber} from '@/components/WdiControls/WdiInputNumber';
import {Enum} from '@/enums/system';
import {SalesContractTerms} from '@/services/contract/salescontractterms';
import {DataOptions} from '@/services/basic/metadata';
import {isDupSalesContractTerms} from '@/pages/contract/SalesContractApply/components/data';
import {Product} from '@/services/mdm/product';
import {ModalAppTypeEnums} from '@/pages/contract/SalesContractApply/components/details';
import {BO} from '@/services/mdm/bo';

const {TextArea} = Input;

const ecDataLoadingFactory = (pageTypeId: number) => {
  return async (params: any) => {
    const response = await EC.searchEc3(
      Object.assign(
        {
          isActive: true,
          pageTypeIds: [pageTypeId].join(),
        },
        params,
      ),
    );
    const {treeData, dict} = FieldProps.TreeSelect.formatTreeData({
      textField: 'ecName',
      valueField: 'ecId',
      id: 'ecId',
      pId: 'parentId',
      level: 'ecLevel',
    }, response.data, (node: any, item: any) => {
      node.title = (
        <Tooltip placement="topLeft" title={item.remark}><span>{node.title}</span></Tooltip>
      );
      return node;
    });
    return {treeData, dict}
  };
};

const processModeDataLoading = async (params: any) => {
  const response = await DataOptions.search(Object.assign({ typeGroup: 13 }, params));
  return response.data.map((item) => ({ value: item.value, label: item.name }));
};

const productTreeDataLoading = async (params: any) => {
  const response = await Product.searchAll(
    Object.assign(
      {
        productLevels: [
          Enum.ProductLevel.PRODUCT1,
          Enum.ProductLevel.PRODUCT2,
          Enum.ProductLevel.PRODUCT3,
          Enum.ProductLevel.PRODUCT4,
          Enum.ProductLevel.PRODUCT5,
        ].join(),
        isActive: true,
      },
      params,
    ),
  );
  const { treeData, dict } = FieldProps.TreeSelect.formatTreeData(
    {
      textField: 'codeOrName',
      valueField: 'productId',
      id: 'productId',
      pId: 'parentId',
      level: 'productLevel',
    },
    response.data,
  );
  return { treeData, dict };
};

const settlementFrequencyDataLoading = async (params: any) => {
  const response = await DataOptions.search(Object.assign({ typeGroup: 15 }, params));
  return response.data.map((item) => ({ value: item.value, label: item.name }));
};

const paymentMethodDataLoading = async (params: any) => {
  const response = await DataOptions.search(
    Object.assign({ typeGroup: 5, inValues: '1,2,3,5,7' }, params),
  );
  return response.data.map((item) => ({ value: item.value, label: item.name }));
};

const SalesContractTermsNonAutoEditApp: React.FC<WdiModalAppProps> = (props) => {
  const formRef = useRef<ProFormInstance>();
  const [editMode, setEditMode] = useState<boolean>();
  const [expandKeysProductRate, setExpandKeysProductRate] = useState<string[]>();
  const [expandKeysBo, setExpandKeysBo] = useState<string[]>();
  const { soldToCode, soldToGroupId } = props.data;
  const [reloadKeys, setReloadKeys] = useState<any>();

  const ecDataLoading = useMemo(() => {
    return ecDataLoadingFactory(props.data?.pageTypeId);
  }, [props.data?.pageTypeId]);

  useEffect(() => {
    formRef.current?.setFieldsValue({
      processMode: props?.data?.row?.processMode,
    });
    if (soldToCode) {
      setReloadKeys(soldToCode);
    } else {
      setReloadKeys(soldToGroupId);
    }
    if (props.data.row) {
      const type = Number(props?.type);
      setEditMode(type == ModalAppTypeEnums.EditNonAuto);
      const detail = props.data.row;
      formRef.current?.setFieldsValue({
        salesContractTermsId: detail.salesContractTermsId,
        salesContractId: detail.salesContractId,
        ecId: detail.ecId,
        revenueType: detail.revenueType,
        settlementFrequency: detail.settlementFrequency,
        paymentMethodId: detail.paymentMethodId,
        termsAmountMonth: detail.termsAmountMonth,
        termsAmount: detail.termsAmount,
        remark: detail.remark,
      });

      if (detail.productIdsRebate) {
        Product.searchAll({ productIds: detail.productIdsRebate }).then((response) => {
          if (response.data && response.data.length > 0) {
            setExpandKeysProductRate(response.data.map((item) => `${item.productId}`));
            formRef.current?.setFieldsValue({
              productIdsRebate: response.data.map((product) => ({
                value: product.productId,
                label: product.codeOrName,
              })),
            });
          }
        });
      }
      if (detail.boId) {
        BO.get(detail.boId).then((res) => {
          setExpandKeysBo([String(res.data.boId)]);
          formRef.current?.setFieldsValue({
            boId: { value: res.data.boId, label: res.data.boName },
          });
        });
      }
    }
  }, [props.data.row, props.type]);

  return (
    <WdiModalForm
      {...HorzLayoutOpts}
      formRef={formRef}
      title={props.title}
      visible={props.visible}
      preserve={true}
      modalProps={{
        onCancel: () => {
          props.onClose ? props.onClose() : null;
        },
      }}
      onFinish={async (values) => {
        const { productIdsRebate, productIdsReach, soldToCode, soldToGroupId, boId, ...entity } =
          values;
        if (productIdsRebate) {
          entity.productIdsRebate = productIdsRebate.map((item: any) => item.value).join();
          entity.productIdsReach = productIdsRebate.map((item: any) => item.value).join();
        }
        if (boId) {
          entity.boId = WdiSelectFormat.getvalue(boId);
        }
        if (soldToCode) {
          entity.soldToCode = soldToCode;
        }
        if (soldToGroupId) {
          entity.soldToGroupId = soldToGroupId;
        }
        entity.revenueType = Enum.RevenueType.FIXED;

        const salesContract = {
          pageTypeId: props.data?.pageTypeId,
          soldToCode: props.data?.soldToCode,
          soldToGroupId: props.data?.soldToGroupId,
          custSysCode: props.data?.custSysCode,
        };
        const response = await SalesContractTerms.validateDetail({
          salesContractTerms: entity,
          salesContract: salesContract,
        });

        if (response.code !== ResponseStatus.SUCCESS) {
          message.error(response.message);
        } else {
          if (isDupSalesContractTerms(props.data.rows, response.data, props.data.rowIndex)) {
            message.error('合同条款已存在');
          } else {
            if (props.onSave) {
              props.onSave({
                code: ResponseStatus.SUCCESS,
                message: '',
                data: response.data,
              });
            }
          }
        }
      }}
    >
      <Form.Item name="salesContractTermsId" label="salesContractTermsId" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="salesContractId" label="salesContractId" hidden>
        <Input />
      </Form.Item>
      <Form.Item
        required
        name="ecId"
        label="费用项"
        rules={[{ required: true, message: '费用项不能为空!' }]}
      >
        <WdiTreeSelect
          {...{
            placeholder: '请选择费用项',
            dataLoading: ecDataLoading,
          }}
        />
      </Form.Item>
      <Form.Item
        name="processMode"
        label="处理方式"
        rules={[{ required: true, message: '处理方式不能为空!' }]}
      >
        <WdiSelect
          {...{
            placeholder: '请选择处理方式',
            disabled: true,
            dataLoading: processModeDataLoading,
          }}
        />
      </Form.Item>
      <Form.Item
        name="productIdsRebate"
        label="返利产品"
        rules={[{ required: true, message: '返利产品不能为空!' }]}
      >
        <WdiTreeSelect
          {...{
            multiple: true,
            placeholder: '请选择返利产品',
            defaultExpandKeys: expandKeysProductRate,
            dataLoading: productTreeDataLoading,
            onExSelect: (value: any, node: any, dict: any) => {
              const values = formRef.current?.getFieldValue('productIdsRebate');
              const rtv = FieldProps.TreeSelect.labeledValuesMeger(dict, values, { value });
              formRef.current?.setFieldsValue({
                productIdsRebate: rtv,
              });
            },
          }}
        />
      </Form.Item>
      <Form.Item name="boId" label="四级使用部门" rules={[{ required: true }]}>
        <WdiTreeSelect
          {...{
            placeholder: '请选择四级使用部门',
            defaultExpandKeys: expandKeysBo,
            labelInValue: true,
            reloadKey: reloadKeys,
            dataLoading: async (params) => {
              const response = await BO.search(
                Object.assign(
                  {
                    soldToCode: soldToCode,
                    soldToGroupId: soldToGroupId,
                    isActive: true,
                    boLevels: '1,2,3,4',
                  },
                  params,
                ),
              );
              const data = response.data;
              const { treeData, dict } = FieldProps.TreeSelect.formatTreeData(
                {
                  textField: 'boName',
                  valueField: 'boId',
                  id: 'boId',
                  pId: 'parentId',
                  level: 'boLevel',
                  disabledLevel: [0, 1, 2, 3],
                },
                data,
              );
              return { treeData, dict };
            },
          }}
        />
      </Form.Item>
      <Form.Item name="settlementFrequency" label="结案频率" hidden>
        <WdiSelect
          {...{
            placeholder: '请选择结案频率',
            disabled: editMode,
            dataLoading: settlementFrequencyDataLoading,
          }}
        />
      </Form.Item>
      <Form.Item
        required
        name="paymentMethodId"
        label="支付方式"
        rules={[{ required: true, message: '支付方式不能为空!' }]}
      >
        <WdiSelect
          {...{
            placeholder: '请选择支付方式',
            dataLoading: paymentMethodDataLoading,
          }}
        />
      </Form.Item>
      <Form.Item
        required
        name="termsAmountMonth"
        label="每月固定金额"
        rules={[{ required: true, message: '每月固定金额不能为空!' }]}
      >
        <WdiInputNumber format="d" digits={2} min={0} />
      </Form.Item>
      <Form.Item
        required
        name="termsAmount"
        label="总固定金额"
        rules={[{ required: true, message: '总固定金额不能为空!' }]}
      >
        <WdiInputNumber format="d" digits={2} min={0} />
      </Form.Item>
      <Form.Item name="remark" label="备注">
        <TextArea rows={3} style={{ width: '100%' }} />
      </Form.Item>
    </WdiModalForm>
  );
};

export default SalesContractTermsNonAutoEditApp;
